import React from "react";
import { FaPlay, FaHeadset, FaEnvelope, FaPhone } from "react-icons/fa";

export default function AboutUs() {
  return (
    <div className="max-w-6xl mx-auto px-4 py-12 bg-black text-gray-100">

      <h1 className="text-4xl md:text-5xl font-bold mb-8 text-transparent bg-clip-text bg-gradient-to-r from-[#38BDF8] to-[#FF1493]">
        About SourceCodey
      </h1>

      <div className="space-y-8 text-lg">

        <section>
          <h2 className="text-3xl font-bold mb-6 text-white">
            Welcome to SourceCodey
          </h2>

          <p className="leading-relaxed">
            SourceCodey is a modern digital marketplace created for developers,
            entrepreneurs, and startups who want to build products faster and
            launch businesses smarter. Our platform connects creators from
            around the world who build powerful digital products with buyers
            looking for ready-to-use software solutions.
          </p>

          <p className="leading-relaxed mt-4">
            Instead of starting every project from scratch, SourceCodey allows
            developers to buy, sell, and monetize high-quality source code,
            templates, tools, and digital assets that accelerate development.
            Whether you're building a startup, learning programming, or selling
            your own digital creations, SourceCodey provides the ecosystem to
            make it happen.
          </p>
        </section>


        <section>
          <h2 className="text-3xl font-bold mb-6 text-white">
            What You Can Find on SourceCodey
          </h2>

          <p className="mb-6">
            Our marketplace is carefully designed to support developers,
            designers, and founders with powerful digital products across
            multiple categories:
          </p>

          <div className="grid md:grid-cols-2 gap-6">

            <div className="bg-gray-900 p-6 rounded-xl border border-gray-800">
              <h3 className="font-bold text-xl flex items-center mb-3 text-[#38BDF8]">
                <FaPlay className="mr-3" />
                App & Web Templates
              </h3>
              <p>
                Discover professionally built Mobile Apps and Web Apps ready
                for customization, helping developers launch products in a
                fraction of the time.
              </p>
            </div>

            <div className="bg-gray-900 p-6 rounded-xl border border-gray-800">
              <h3 className="font-bold text-xl flex items-center mb-3 text-[#FF1493]">
                <FaHeadset className="mr-3" />
                Backend Systems & APIs
              </h3>
              <p>
                Powerful backend infrastructures, authentication systems,
                APIs, and scalable server architectures for modern
                applications.
              </p>
            </div>

            <div className="bg-gray-900 p-6 rounded-xl border border-gray-800">
              <h3 className="font-bold text-xl flex items-center mb-3 text-[#38BDF8]">
                <FaPlay className="mr-3" />
                AI & Automation Tools
              </h3>
              <p>
                Cutting-edge artificial intelligence tools and automation
                systems designed to enhance productivity and innovation.
              </p>
            </div>

            <div className="bg-gray-900 p-6 rounded-xl border border-gray-800">
              <h3 className="font-bold text-xl flex items-center mb-3 text-[#FF1493]">
                <FaHeadset className="mr-3" />
                UI / UX Kits & Graphic Designs
              </h3>
              <p>
                Beautiful user interface kits, design systems, and graphics
                that help developers and designers create modern digital
                experiences.
              </p>
            </div>

            <div className="bg-gray-900 p-6 rounded-xl border border-gray-800">
              <h3 className="font-bold text-xl flex items-center mb-3 text-[#38BDF8]">
                <FaPlay className="mr-3" />
                Programming Courses
              </h3>
              <p>
                Educational resources created by experienced developers to
                help others learn programming, frameworks, and modern
                development practices.
              </p>
            </div>

            <div className="bg-gray-900 p-6 rounded-xl border border-gray-800">
              <h3 className="font-bold text-xl flex items-center mb-3 text-[#FF1493]">
                <FaHeadset className="mr-3" />
                Startup-Ready Products
              </h3>
              <p>
                Complete startup-ready solutions including mobile apps and
                web platforms designed to help entrepreneurs launch their
                businesses instantly.
              </p>
            </div>

          </div>
        </section>


        <section>
          <h2 className="text-3xl font-bold mb-6 text-white">
            Our Vision
          </h2>

          <p className="text-xl leading-relaxed">
            To become one of the world’s leading digital marketplaces for
            source code and developer products, empowering creators to turn
            their ideas into scalable businesses.
          </p>
        </section>


        <section>
          <h2 className="text-3xl font-bold mb-6 text-white">
            Our Mission
          </h2>

          <ul className="list-disc pl-6 space-y-3 text-lg">
            <li>To empower developers to monetize their code globally</li>
            <li>To help startups build and launch products faster</li>
            <li>To create a trusted ecosystem for buying and selling digital products</li>
            <li>To support innovation through accessible technology resources</li>
          </ul>
        </section>


        <section>
          <h2 className="text-3xl font-bold mb-6 text-white">
            Why Choose SourceCodey?
          </h2>

          <ul className="space-y-4">

            <li className="flex items-start">
              <div className="bg-[#38BDF8] p-1 rounded-full mr-4 mt-1">
                <FaPlay className="text-white text-sm" />
              </div>
              <span>
                <strong className="text-white">
                  Developer-Focused Marketplace
                </strong>{" "}
                — Built specifically for developers, designers, and tech founders.
              </span>
            </li>

            <li className="flex items-start">
              <div className="bg-[#FF1493] p-1 rounded-full mr-4 mt-1">
                <FaPlay className="text-white text-sm" />
              </div>
              <span>
                <strong className="text-white">
                  High-Quality Digital Products
                </strong>{" "}
                — Carefully structured categories that make discovering
                reliable code assets easier.
              </span>
            </li>

            <li className="flex items-start">
              <div className="bg-[#38BDF8] p-1 rounded-full mr-4 mt-1">
                <FaPlay className="text-white text-sm" />
              </div>
              <span>
                <strong className="text-white">
                  Faster Product Development
                </strong>{" "}
                — Save hundreds of development hours by starting with
                production-ready code.
              </span>
            </li>

            <li className="flex items-start">
              <div className="bg-[#FF1493] p-1 rounded-full mr-4 mt-1">
                <FaPlay className="text-white text-sm" />
              </div>
              <span>
                <strong className="text-white">
                  Global Creator Community
                </strong>{" "}
                — Developers worldwide can sell their code and earn from
                their skills.
              </span>
            </li>

          </ul>
        </section>


        <section className="bg-gradient-to-r from-[#38BDF8] to-[#FF1493] text-white p-8 rounded-xl">

          <div className="flex flex-col md:flex-row items-center">

            <div className="mb-6 md:mb-0 md:mr-8">
              <h2 className="text-3xl font-bold mb-3">
                "Build Faster. Launch Smarter."
              </h2>
              <p className="text-xl opacity-90">
                Where developers turn code into businesses.
              </p>
            </div>

            <div className="space-y-3">

              <div className="flex items-center">
                <FaEnvelope className="mr-3" />
                <span>sourcecodey.com@gmail.com</span>
              </div>

              <div className="flex items-center">
                <FaPhone className="mr-3" />
                <span>+256 708 435 802</span>
              </div>

            </div>
          </div>

          <p className="mt-6 text-center text-lg font-medium">
            🚀 SourceCodey — The Marketplace Where Code Powers Innovation
          </p>

        </section>

      </div>
    </div>
  );
}